import React, { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Link, useNavigate } from 'react-router-dom'
import {
  Bot, Database, Boxes, Workflow, Plug, Route, Terminal,
  Check, Lock, Loader2, CreditCard, ArrowLeft, X, MessageSquare, Sparkles, ExternalLink
} from 'lucide-react'
import { getSession } from '../lib/session'
import ProductGuide from '../components/ProductGuide'
import { sampleFor } from '../lib/productGuides'
import { unitOf } from '../lib/serviceCatalog'
import { setQty } from '../lib/cart'

// Icon + accent per service key (catalog supplies title/description/price).
const SERVICE_ICON = {
  answers: Bot, sync: Database, rag: Boxes, orchestration: Workflow,
  middleware: Plug, intake: Route, agent: Terminal
}

const STATUS_BADGE = {
  active:          { label: 'Active',          cls: 'bg-green-500/15 text-green-600' },
  pending_payment: { label: 'Payment pending', cls: 'bg-amber-500/15 text-amber-600' },
  provisioning:    { label: 'Provisioning…',   cls: 'bg-blue-500/15 text-blue-600' },
  failed:          { label: 'Failed',          cls: 'bg-red-500/15 text-red-600' },
  inactive:        { label: 'Available',       cls: 'bg-gray-200 text-gray-600' }
}

// axios that doesn't throw on 402/403 — we read those bodies as real responses.
const soft = { validateStatus: (s) => s < 500 }

export default function Services() {
  const session = getSession()
  const navigate = useNavigate()
  const ns = session?.namespaceId
  const [data, setData] = useState(null)   // { plan, models, billingTestMode, services }
  const [instances, setInstances] = useState([])
  const [loading, setLoading] = useState(true)
  const [flow, setFlow] = useState(null)   // activation modal state
  const [useSvc, setUseSvc] = useState(null) // service being "used" (chat drawer)

  const load = async () => {
    if (!ns) { setLoading(false); return }
    try {
      const [svc, inst] = await Promise.all([
        axios.get(`/api/v1/tenants/${ns}/services`),
        axios.get(`/api/v1/tenants/${ns}/instances`).catch(() => ({ data: { instances: [] } })),
      ])
      setData(svc.data)
      setInstances(inst.data.instances || [])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load services')
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [ns])

  // Buy one more instance seat for an already-active service (prorated on Stripe,
  // free bump in test mode). The count is the authoritative new total.
  // Buying another instance is a purchase → it goes through CHECKOUT, not a
  // silent seat bump. Put the new total quantity in the cart and send them to pay.
  const addSeat = (svc) => {
    const unit = unitOf(svc.service)
    setQty(svc.service, (svc.seats || 1) + 1)
    toast.success(`Another ${unit} added to your cart — checkout to activate it`)
    navigate('/checkout')
  }

  if (!ns) {
    return (
      <div className="card bg-amber-500/10 border-amber-500/30 text-sm text-amber-600 max-w-lg">
        No workspace connected. Go to <Link to="/setup" className="font-semibold underline">Workspace</Link> to get started.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Services</h2>
          <p className="text-gray-500 mt-1">Pick a service, choose a model, activate it for your site.</p>
        </div>
        {data && (() => {
          const active = (data.services || []).filter((s) => s.activated)
          const spend = active.reduce((sum, s) => sum + (Number(s.priceUsd) || 0), 0)
          return (
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-primary-500/10 text-primary-600">
              {active.length} active · ${spend}/mo
            </span>
          )
        })()}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500"><Loader2 className="animate-spin" size={18} /> Loading services…</div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {data.services.map((svc) => (
            <ServiceCard key={svc.service} svc={svc}
              onActivate={() => setFlow({ svc, step: 'configure', model: (data.models.find(m => m.default) || data.models[0])?.key,
                                          rootUrl: svc.sourceUrl || '', activation: null, busy: false,
                                          sourceMode: instances.length ? 'existing' : 'new',
                                          instanceId: (instances.find(i => i.isDefault) || instances[0])?.id || '' })}
              onAddSeat={() => addSeat(svc)}
              onUse={() => setUseSvc(svc)} />
          ))}
        </div>
      )}

      {flow && (
        <ActivateModal ns={ns} models={data.models} testMode={data.billingTestMode}
          services={data.services} instances={instances} flow={flow} setFlow={setFlow}
          onDone={() => { setFlow(null); load() }} />
      )}
      {useSvc && <UseDrawer svc={useSvc} onClose={() => setUseSvc(null)} />}
    </div>
  )
}

function ServiceCard({ svc, onActivate, onAddSeat, onUse }) {
  const Icon = SERVICE_ICON[svc.service] || Sparkles
  const locked = false // à-la-carte: every service is activatable, never plan-locked
  const badge = STATUS_BADGE[svc.status] || STATUS_BADGE.inactive
  const unit = unitOf(svc.service)

  return (
    <div className={`card flex flex-col ${locked ? 'opacity-70' : ''}`}>
      <div className="flex items-start justify-between">
        <div className={`p-2.5 rounded-xl ${svc.activated ? 'bg-primary-500/10' : 'bg-gray-100'}`}>
          <Icon className={svc.activated ? 'text-primary-600' : 'text-gray-500'} size={24} />
        </div>
        {locked
          ? <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-gray-200 text-gray-500"><Lock size={12} /> Not in plan</span>
          : <span className={`text-xs font-medium px-2 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>}
      </div>

      <h3 className="text-lg font-semibold text-gray-900 mt-3">{svc.title}</h3>
      <p className="text-sm text-gray-500 mt-1 flex-1">{svc.description}</p>

      <div className="flex items-center justify-between mt-4">
        <span className="text-sm text-gray-900 font-semibold">
          {svc.priceUsd != null ? <>${svc.priceUsd}<span className="text-gray-400 font-normal">/mo</span></> : '—'}
        </span>
        {svc.activated && svc.model && (
          <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-500 capitalize">{svc.model} model</span>
        )}
      </div>

      {/* Seat meter — services you can run several of */}
      {svc.status === 'active' && svc.seated && (
        <div className="mt-3 flex items-center justify-between text-xs bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
          <span className="text-gray-500">
            <span className="font-semibold text-gray-800 tabular-nums">{svc.seatsUsed ?? 0}</span> of <span className="tabular-nums">{svc.seats ?? 1}</span> {unit}{(svc.seats ?? 1) > 1 ? 's' : ''} in use
          </span>
          <button onClick={onAddSeat} className="font-semibold text-primary-600 hover:underline whitespace-nowrap">+ Buy another</button>
        </div>
      )}
      {svc.status === 'active' && !svc.seated && (
        <div className="mt-3 text-xs text-gray-400 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
          One per workspace — runs across all your instances. No extra seats needed.
        </div>
      )}

      <div className="mt-4">
        {locked ? (
          <button disabled className="w-full btn-primary opacity-50 cursor-not-allowed">Upgrade to unlock</button>
        ) : svc.status === 'active' ? (
          <button onClick={onUse} className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            {svc.service === 'answers' ? <><MessageSquare size={16} /> Open chat</> : <><ExternalLink size={16} /> How to use</>}
          </button>
        ) : svc.status === 'pending_payment' ? (
          <button onClick={onActivate} className="w-full btn-primary flex items-center justify-center gap-2">
            <CreditCard size={16} /> Complete payment
          </button>
        ) : (
          <button onClick={onActivate} className="w-full btn-primary flex items-center justify-center gap-2">
            <Sparkles size={16} /> Activate
          </button>
        )}
      </div>
    </div>
  )
}

function ActivateModal({ ns, models, testMode, services = [], instances = [], flow, setFlow, onDone }) {
  const { svc } = flow
  const set = (patch) => setFlow((f) => ({ ...f, ...patch }))
  const isNewSource = flow.sourceMode === 'new'
  const [cart, setCart] = useState([]) // extra service keys added at checkout
  const toggleCart = (key) => setCart((c) => c.includes(key) ? c.filter((k) => k !== key) : [...c, key])

  // Services the visitor can add on the checkout page (not the primary, not already on).
  const addOns = services.filter((s) => s.service !== svc.service && !s.activated)
  const selected = [svc, ...addOns.filter((s) => cart.includes(s.service))]
  const total = selected.reduce((sum, s) => sum + (Number(s.priceUsd) || 0), 0)

  // Attach payload: an EXISTING instance (share its source) or a NEW source.
  const sourcePayload = () => isNewSource
    ? { rootUrl: flow.rootUrl.trim() || undefined }
    : { instanceId: flow.instanceId || undefined }

  // Step 1 → create/refresh the pending order, then move to checkout.
  const toCheckout = async () => {
    if (isNewSource && svc.ingests && !flow.rootUrl.trim()) { toast.error('Enter the website URL for the new source (or switch to an existing source)'); return }
    if (!isNewSource && !flow.instanceId) { toast.error('Pick an existing source, or choose New source'); return }
    set({ busy: true })
    try {
      const res = await axios.post(`/api/v1/tenants/${ns}/services/${svc.service}/activate`,
        { ...sourcePayload(), model: flow.model }, soft)
      if (res.status === 403) { toast.error(res.data.error); set({ busy: false }); return }
      set({ activation: res.data.activation, step: 'checkout', busy: false })
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not start activation'); set({ busy: false })
    }
  }

  // Step 2 → pay. If Stripe is configured, go to real Checkout; otherwise
  // (billing not configured / test mode) provision directly.
  const pay = async () => {
    set({ busy: true })
    const list = [svc.service, ...cart]
    const src = sourcePayload()
    try {
      const co = await axios.post('/api/v1/stripe/services-checkout',
        { namespaceId: ns, services: list, model: flow.model, ...src }, soft)
      if (co.status < 400 && co.data?.url) { window.location.href = co.data.url; return }
      // 503 = billing not configured → direct (test-mode) grant. The primary was
      // already activated with its source in step 1; just confirm it. Add-ons
      // activate (same source) + confirm.
      for (const service of list) {
        if (service !== svc.service) {
          await axios.post(`/api/v1/tenants/${ns}/services/${service}/activate`, { ...src, model: flow.model }, soft)
        }
        await axios.post(`/api/v1/tenants/${ns}/services/${service}/confirm`, { simulatePayment: true }, soft)
      }
      toast.success(list.length > 1 ? `${list.length} services activated` : `${svc.title} activated`)
      onDone()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Activation failed'); set({ busy: false })
    }
  }

  const modelObj = models.find((m) => m.key === flow.model)

  return (
    <Overlay onClose={() => setFlow(null)}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold text-gray-900">
          {flow.step === 'checkout' ? 'Checkout' : `Activate ${svc.title}`}
        </h3>
        <button onClick={() => setFlow(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
      </div>

      {flow.step === 'configure' && (
        <div className="space-y-5 mt-4">
          {/* Source chooser — attach to an EXISTING instance (shared data) or a NEW source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data source</label>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-2">
              {[['existing', 'Existing source'], ['new', 'New source']].map(([k, label]) => (
                <button key={k} type="button" onClick={() => set({ sourceMode: k })}
                  disabled={k === 'existing' && instances.length === 0}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium ${flow.sourceMode === k ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'} disabled:opacity-40`}>
                  {label}
                </button>
              ))}
            </div>
            {!isNewSource ? (
              <>
                <select className="input" value={flow.instanceId || ''} onChange={(e) => set({ instanceId: e.target.value })}>
                  {instances.map((i) => <option key={i.id} value={i.id}>{i.name}{i.sourceUrl ? ` — ${i.sourceUrl}` : ' — standalone'}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">This service will share the knowledge already on that source. Nothing re-crawled.</p>
              </>
            ) : (
              <>
                {svc.ingests
                  ? <input type="url" className="input" placeholder="https://yourcompany.com"
                      value={flow.rootUrl} onChange={(e) => set({ rootUrl: e.target.value })} />
                  : <p className="text-xs text-gray-500">A new instance will be created; connect its content after from <span className="font-medium">Instances</span>.</p>}
                <p className="text-xs text-gray-400 mt-1">Creates a new instance for this source — it won’t touch your other sources.</p>
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">AI model</label>
            <div className="grid grid-cols-2 gap-2">
              {models.map((m) => (
                <button key={m.key} onClick={() => set({ model: m.key })}
                  className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${
                    flow.model === m.key ? 'border-primary-600 bg-primary-500/5' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{m.label}</span>
                    {flow.model === m.key && <Check size={15} className="text-primary-600" />}
                  </div>
                  <span className="text-[11px] text-gray-500">
                    {m.weight}× credits{m.default ? ' · included' : ''}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">Heavier models give better answers but use your message credits faster.</p>
          </div>

          <button onClick={toCheckout} disabled={flow.busy} className="btn-primary w-full disabled:opacity-60 flex items-center justify-center gap-2">
            {flow.busy ? <Loader2 className="animate-spin" size={16} /> : null}
            Continue to payment
          </button>
        </div>
      )}

      {flow.step === 'checkout' && (
        <div className="space-y-4 mt-4">
          <button onClick={() => set({ step: 'configure' })} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800">
            <ArrowLeft size={13} /> Back
          </button>

          {/* Cart summary */}
          <div className="rounded-xl border border-gray-200 divide-y divide-gray-200">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-gray-700">{svc.title} <span className="text-gray-400">· {modelObj?.label}</span></span>
              <span className="text-sm text-gray-700">${svc.priceUsd}/mo</span>
            </div>
            {addOns.filter((s) => cart.includes(s.service)).map((s) => (
              <div key={s.service} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-gray-700">{s.title}</span>
                <span className="flex items-center gap-2 text-sm text-gray-700">${s.priceUsd}/mo
                  <button onClick={() => toggleCart(s.service)} className="text-gray-300 hover:text-red-500"><X size={14} /></button>
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm font-semibold text-gray-900">Total</span>
              <span className="text-lg font-bold text-gray-900">${total}<span className="text-gray-400 text-sm font-normal">/mo</span></span>
            </div>
          </div>

          {/* Add more services — no need to go back to the catalog */}
          {addOns.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">Add more services — pay for everything in one go, cancel any anytime</p>
              <div className="space-y-1.5 max-h-44 overflow-y-auto pr-0.5">
                {addOns.map((s) => {
                  const inCart = cart.includes(s.service)
                  return (
                    <button key={s.service} onClick={() => toggleCart(s.service)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-colors ${inCart ? 'border-primary-600 bg-primary-500/5' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <span className="flex items-center gap-2.5">
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${inCart ? 'bg-primary-600 border-primary-600' : 'border-gray-300'}`}>
                          {inCart && <Check size={11} className="text-white" />}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{s.title}</span>
                      </span>
                      <span className="text-sm text-gray-600">+${s.priceUsd}/mo</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <button onClick={pay} disabled={flow.busy} className="btn-primary w-full disabled:opacity-60 flex items-center justify-center gap-2">
            {flow.busy ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
            Pay ${total}/mo &amp; activate{selected.length > 1 ? ` ${selected.length} services` : ''}
          </button>
          {testMode
            ? <p className="text-xs text-center text-amber-600">Test mode — no real charge. Stripe replaces this at launch.</p>
            : <p className="text-xs text-center text-gray-400">Secure payment.</p>}
        </div>
      )}
    </Overlay>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 font-medium truncate max-w-[60%] text-right">{value}</span>
    </div>
  )
}

// "Use" the activated service. For answers → live chat; others → the manifest (how to call it).
function UseDrawer({ svc, onClose }) {
  const isChat = svc.service === 'answers'
  const instanceId = svc.manifest?.chatbotId
  const ns = getSession()?.namespaceId
  const [tab, setTab] = useState('install') // install | preview
  const [msgs, setMsgs] = useState([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const endRef = useRef(null)
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [msgs])

  // Domain allowlist for the embedded bot.
  const [domains, setDomains] = useState(null)
  const [newDomain, setNewDomain] = useState('')
  useEffect(() => {
    if (!isChat || !ns) return
    axios.get(`/api/v1/tenants/${ns}/bot/domains`).then((r) => setDomains(r.data.domains || [])).catch(() => setDomains([]))
  }, [isChat, ns])
  const saveDomains = async (next) => {
    setDomains(next)
    try { await axios.put(`/api/v1/tenants/${ns}/bot/domains`, { domains: next }) }
    catch { toast.error('Could not save domains') }
  }
  const addDomain = () => {
    const d = newDomain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
    if (!d) return
    if (domains?.includes(d)) { setNewDomain(''); return }
    saveDomains([...(domains || []), d]); setNewDomain('')
  }

  // Public bot id = base64url(namespace|instanceId) — safe to expose, no API key.
  const publicId = (ns && instanceId)
    ? btoa(`${ns}|${instanceId}`).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
    : null
  const snippet = `<script src="${window.location.origin}/widget.js" data-bot="${publicId}" async></script>`

  const send = async (e, forced) => {
    if (e) e.preventDefault()
    const q = (forced || input).trim()
    if (!q || busy) return
    setInput(''); setMsgs((m) => [...m, { role: 'user', content: q }]); setBusy(true)
    try {
      const { data } = await axios.post('/api/v1/answers/chat', { instanceId, message: q })
      setMsgs((m) => [...m, { role: 'assistant', content: data.message || data.answer || '(no response)' }])
    } catch (err) {
      setMsgs((m) => [...m, { role: 'assistant', content: err.response?.data?.error || 'Chat failed.' }])
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-lg bg-gray-50 h-full shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-900">{svc.title}</h3>
            <p className="text-xs text-gray-500">{svc.model} model{svc.sourceUrl ? ` · ${svc.sourceUrl}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        {isChat ? (
          <>
            {/* what it is + how it works — consistent with the other services */}
            <div className="px-4 pt-4">
              <p className="text-sm text-gray-600 mb-2">{svc.description}</p>
              <ol className="space-y-1.5">
                {['Trains on your site automatically — try it in Preview.', 'Paste one snippet on your site (Install) to go live for visitors.'].map((s, i) => (
                  <li key={i} className="flex gap-2 text-sm text-gray-700">
                    <span className="flex-none w-5 h-5 rounded-full bg-primary-500/10 text-primary-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>{s}
                  </li>
                ))}
              </ol>
            </div>

            <div className="flex gap-1 bg-gray-100 p-1 m-4 mb-0 rounded-lg w-fit">
              {['install', 'preview'].map((t) => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-gray-50 text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>
                  {t === 'install' ? 'Install on your site' : 'Preview'}
                </button>
              ))}
            </div>

            {tab === 'install' ? (
              <div className="p-4 space-y-4 overflow-y-auto">
                <p className="text-sm text-gray-600">Paste this snippet into your website's HTML, just before <code className="text-xs bg-gray-100 px-1 rounded">&lt;/body&gt;</code>. A chat bubble appears on every page.</p>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-3 pr-10 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all">{snippet}</pre>
                  <button onClick={() => { navigator.clipboard.writeText(snippet); toast.success('Snippet copied') }}
                    className="absolute top-2 right-2 text-xs bg-gray-700 text-gray-100 px-2 py-1 rounded hover:bg-gray-600">Copy</button>
                </div>
                <div className="rounded-lg bg-primary-500/10 border border-primary-500/20 p-3">
                  <p className="text-xs text-primary-700">The bubble trains on <span className="font-semibold">{svc.sourceUrl || 'your site'}</span> and stays fresh with daily auto-refresh. Switch to <button onClick={() => setTab('preview')} className="underline font-medium">Preview</button> to test it here first.</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Allowed domains</p>
                  <p className="text-xs text-gray-500 mb-2">The bot only answers when embedded on these domains. Leave empty to allow any site.</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(domains || []).map((d) => (
                      <span key={d} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {d}
                        <button onClick={() => saveDomains(domains.filter((x) => x !== d))} className="text-gray-400 hover:text-red-600"><X size={12} /></button>
                      </span>
                    ))}
                    {domains && domains.length === 0 && <span className="text-xs text-amber-600">Open — any site can embed this bot.</span>}
                    {domains === null && <span className="text-xs text-gray-400">Loading…</span>}
                  </div>
                  <div className="flex gap-2">
                    <input className="input flex-1 text-sm" placeholder="example.com" value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addDomain()} />
                    <button onClick={addDomain} className="btn-primary px-4 text-sm">Add</button>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Options</p>
                  <p className="text-xs text-gray-500">Add <code className="bg-gray-100 px-1 rounded">data-accent="#0ea5e9"</code> to the tag to match your brand color.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {svc.manifest?.greeting && msgs.length === 0 && (
                    <div className="flex justify-start"><div className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-bl-md text-sm bg-gray-100 text-gray-800">{svc.manifest.greeting}</div></div>
                  )}
                  {msgs.length === 0 && (
                    <button onClick={() => send(null, sampleFor('answers').query)} disabled={busy}
                      className="self-start flex items-center gap-1.5 text-xs font-semibold text-primary-600 border border-primary-600/30 rounded-full px-3 py-1.5 hover:bg-primary-600 hover:text-white transition-colors disabled:opacity-50">
                      <Sparkles size={12} /> Try the example
                    </button>
                  )}
                  {msgs.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${m.role === 'user' ? 'bg-primary-600 text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'}`}>{m.content}</div>
                    </div>
                  ))}
                  {busy && <div className="flex justify-start"><div className="px-4 py-2.5 rounded-2xl bg-gray-100 text-gray-400 text-sm animate-pulse">…</div></div>}
                  <div ref={endRef} />
                </div>
                <form onSubmit={send} className="p-4 border-t border-gray-200 flex gap-2">
                  <input className="input flex-1" placeholder="Ask about your site…" value={input} onChange={(e) => setInput(e.target.value)} disabled={busy} />
                  <button className="btn-primary px-5 disabled:opacity-50" disabled={busy || !input.trim()}><MessageSquare size={16} /></button>
                </form>
              </>
            )}
          </>
        ) : (
          <NonChatConsole svc={svc} />
        )}
      </div>
    </div>
  )
}

// Plain-language "how it works" + a real try-it panel + copyable API — per service.
function NonChatConsole({ svc }) {
  const m = svc.manifest || {}
  const [q, setQ] = useState('')
  const [doc, setDoc] = useState('')
  const [out, setOut] = useState(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')

  const run = async (fn) => { setBusy(true); setNote(''); setOut(null); try { await fn() } catch (e) { setNote(e.response?.data?.error || 'Something went wrong.') } finally { setBusy(false) } }
  const sample = sampleFor(svc.service)

  // One runner per service, taking the input text — reused by the button AND the
  // one-click "Try the example" so both do exactly the same thing.
  const execute = (text) => run(async () => {
    if (svc.service === 'rag') {
      const { data } = await axios.post(`/api/v1/rag/collections/${m.collectionId}/query`, { query: text })
      setOut({ answer: data.answer, sources: (data.sources || []).length })
    } else if (svc.service === 'sync') {
      const { data } = await axios.post('/api/v1/sync/search', { query: text, topK: 3 })
      setOut({ answer: (data.results || []).map((r, i) => `${i + 1}. ${(r.content || '').slice(0, 160)}…`).join('\n\n') || 'No matches.' })
    } else if (svc.service === 'agent') {
      const { data } = await axios.post('/api/v1/agent/run', { message: text })
      setOut({ answer: data.answer, tools: (data.toolsUsed || []).map((t) => t.tool).join(', ') })
    } else if (svc.service === 'intake') {
      const { data } = await axios.post(`/api/v1/intake/instances/${m.intakeId}/intake`, { callerPhone: '+10000000000', transcript: text })
      setOut({ answer: data.type === 'escalation' ? '⚠️ Escalated to a human.' : `Routed to: ${data.specialty || data.type}` })
    } else if (svc.service === 'orchestration') {
      const { data } = await axios.post(`/api/v1/orchestration/workflows/${m.workflows[0].id}/run`, { input: { message: text } })
      setOut({ answer: typeof data.output === 'string' ? data.output : JSON.stringify(data.output) })
    }
  })
  const addDoc = (content) => run(async () => {
    await axios.post(`/api/v1/rag/collections/${m.collectionId}/documents`, { content, metadata: { title: 'Added from console' } })
    setNote('Added — indexing (a few seconds), then ask below.'); setDoc('')
  })
  const runAdapter = () => run(async () => {
    const { data } = await axios.post(`/api/v1/middleware/adapters/${m.adapterId}/run`, {})
    setOut({ answer: JSON.stringify(data.output || data, null, 2) })
  })
  const tryExample = () => {
    if (svc.service === 'middleware') return runAdapter()
    if (svc.service === 'rag') setDoc(sample.doc || '')
    setQ(sample.query || '')
    execute(sample.query || '')
  }

  const PAGE = { sync: ['/sync', 'Sync'], rag: ['/rag', 'Custom RAG'], orchestration: ['/workflows', 'Workflows'], middleware: ['/adapters', 'Adapters'], intake: ['/intake', 'Intake Systems'] }[svc.service]
  const inputByService = {
    sync: 'Search your site content…', rag: 'Ask your collection a question…',
    agent: 'Ask the agent anything about your site…', intake: 'Type a caller message to route…',
    orchestration: 'Give the workflow an input…',
  }
  const btnByService = { sync: 'Search', rag: 'Ask', agent: 'Run', intake: 'Route', orchestration: 'Run' }

  return (
    <div className="p-5 space-y-5 overflow-y-auto">
      {/* detailed how-it-works + worked example */}
      <ProductGuide product={svc.service} />

      {/* try it — no code needed */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Try it now</p>
          <button disabled={busy} onClick={tryExample}
            className="flex items-center gap-1.5 text-xs font-semibold text-primary-600 border border-primary-600/30 rounded-full px-3 py-1 hover:bg-primary-600 hover:text-white transition-colors disabled:opacity-50">
            <Sparkles size={12} /> Try the example
          </button>
        </div>

        {svc.service === 'rag' && (
          <div className="mb-3 pb-3 border-b border-gray-100">
            <label className="text-xs font-medium text-gray-600">Add a document (optional)</label>
            <textarea className="input h-16 mt-1" placeholder="Paste any text — a policy, FAQ, product doc…" value={doc} onChange={(e) => setDoc(e.target.value)} />
            <button disabled={busy || !doc.trim()} onClick={() => addDoc(doc)} className="btn-primary text-sm mt-2 px-4 py-1.5 disabled:opacity-50">Add to collection</button>
          </div>
        )}

        {svc.service === 'middleware' ? (
          <div>
            <p className="text-xs text-gray-500 mb-2">Send a test payload through your adapter:</p>
            <button disabled={busy} onClick={runAdapter} className="btn-primary text-sm px-4 py-1.5 disabled:opacity-50">Run adapter</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input className="input flex-1" placeholder={inputByService[svc.service]} value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && q.trim() && execute(q)} />
            <button disabled={busy || !q.trim()} onClick={() => execute(q)} className="btn-primary px-4 disabled:opacity-50">{btnByService[svc.service]}</button>
          </div>
        )}

        {busy && <p className="text-sm text-gray-400 mt-3 animate-pulse">Working…</p>}
        {note && <p className="text-sm text-emerald-700 bg-emerald-500/10 rounded-lg px-3 py-2 mt-3">{note}</p>}
        {out && (
          <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-3">
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{out.answer}</p>
            {out.sources != null && <p className="text-xs text-gray-400 mt-2">{out.sources} source(s)</p>}
            {out.tools && <p className="text-xs text-gray-400 mt-2">tools used: {out.tools}</p>}
          </div>
        )}
      </div>

      {/* full manager page */}
      {PAGE && (
        <Link to={PAGE[0]} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 hover:bg-gray-50">
          <span className="text-sm font-medium text-gray-800">Open the full {PAGE[1]} workspace</span>
          <ExternalLink size={16} className="text-gray-400" />
        </Link>
      )}

      {/* developer / API */}
      <details className="rounded-xl border border-gray-200 bg-white">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-700">Developer API (call it from your code)</summary>
        <div className="px-4 pb-4 space-y-3">
          <p className="text-xs text-gray-500">Send your workspace API key as <code className="bg-gray-100 border border-gray-200 rounded px-1">Authorization: Bearer …</code> (find it under Workspace → API key). Example:</p>
          {Object.entries(m.use || {}).map(([k, v]) => (
            <div key={k}>
              <p className="text-xs font-medium text-gray-500 capitalize mb-1">{k}</p>
              <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all">{`curl -X ${String(v).split(' ')[0]} https://ai.reright.io${String(v).split(' ')[1] || ''} \\\n  -H "Authorization: Bearer YOUR_API_KEY" \\\n  -H "Content-Type: application/json"`}</pre>
            </div>
          ))}
        </div>
      </details>
    </div>
  )
}

function Overlay({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-md bg-gray-50 rounded-2xl border border-gray-200 shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}
